import {
  CellRenderer,
  Column,
  EntityCollection,
  usePermissions,
  useRestDataSource
} from '@enisco/mcc-react-lib'
import {LicenseEntity, LicenseInfoDefinition} from './definition'
import LicenseUploadActionButton from './LicenseUploadActionButton'
import LicenseUploadDialog from './LicenseUploadDialog'
import {useState} from 'react'
import {LicenseCard} from './LicenseCard'
import {IconButton, Tooltip, Typography} from '@mui/material'
import {FormattedMessage, useIntl} from 'react-intl'
import {formatGoTime} from './utils'
import {LicenseInfoDialog} from './LicenseInfoDialog'
import {Print} from '@mui/icons-material'
import {usePrintLicense} from './usePrintLicense'

const columnConfig: Column[] = [
  {field: 'parsed.application', name: {en: 'Application', de: 'Anwendung'}},
  {field: 'parsed.customer', name: {en: 'Customer', de: 'Kunde'}},
  {field: 'parsed.description', name: {en: 'Description', de: 'Beschreibung'}},
  {field: 'parsed.expiration', name: {en: 'Expiration', de: 'Ablaufdatum'}}
]

const fixedFields = ['application', 'fileContent']

const LicenseInfoApp = () => {
  const intl = useIntl()
  const [datasource, isLoading] = useRestDataSource<LicenseEntity>(
    LicenseInfoDefinition
  )
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [refreshCount, setRefreshCount] = useState(0)
  const checkPermission = usePermissions()
  const printLicense = usePrintLicense()

  const cellRenderers: Record<string, CellRenderer<any>> = {
    'parsed.expiration': (props) =>
      formatGoTime(props.value as string | undefined, intl) ?? '-'
  }

  const handleUploadDialogClose = () => {
    setRefreshCount((prev) => prev + 1)
    setUploadDialogOpen(false)
  }

  const handlePrint = (license: LicenseEntity) => {
    printLicense(license.application)
  }

  if (isLoading) {
    return <></>
  }

  return (
    <>
      <EntityCollection
        refreshCounter={refreshCount}
        dataSource={datasource}
        sx={{height: '100vh'}}
        columns={columnConfig}
        canDelete={() => false}
        canEdit={() => false}
        canDuplicate={() => false}
        parameterScope={'licenses'}
        editCardConfig={false}
        editColumnConfig={false}
        customAddAction={() =>
          checkPermission('mcc-portal#_licenses#_edit') ? (
            <LicenseUploadActionButton
              onClick={() => setUploadDialogOpen(true)}
            />
          ) : (
            <></>
          )
        }
        cardHead={(e) => (
          <Typography variant='h5' align='left' gutterBottom noWrap>
            {e.parsed.application}
          </Typography>
        )}
        fixedCardFields={fixedFields}
        fixedTableFields={fixedFields}
        cardBody={(e) => <LicenseCard license={e} />}
        cardViewSx={{padding: 2}}
        editor={(ctx) => (
          <LicenseInfoDialog
            license={ctx.entity as LicenseEntity}
            open={ctx.open}
            onClose={ctx.onClose}
          />
        )}
        cellRenderers={cellRenderers}
        customActions={[
          (entity) => (
            <Tooltip
              title={
                <FormattedMessage
                  id={'LicenseApp.PrintButton.Tooltip'}
                  defaultMessage='Print this license'
                />
              }
            >
              <span style={{marginLeft: 0}}>
                <IconButton
                  data-testid={'ExportButton'}
                  onClick={() => handlePrint(entity)}
                  size={'large'}
                >
                  <Print />
                </IconButton>
              </span>
            </Tooltip>
          )
        ]}
      />
      <LicenseUploadDialog
        open={uploadDialogOpen}
        onClose={handleUploadDialogClose}
      />
    </>
  )
}

export default LicenseInfoApp
